import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { ArrowLeft, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuthCtx } from "@/lib/auth-context";
import { seo } from "@/lib/seo";
import { ContatoSkeleton } from "@/components/skeletons";

export const Route = createFileRoute("/contato")({
  head: () =>
    seo({
      path: "/contato",
      title: "Contato e suporte — Seu Bolso Tranquilo",
      description:
        "Fale com o suporte do Seu Bolso Tranquilo. Tire dúvidas sobre o app de controle financeiro pessoal, envie sugestões ou peça ajuda.",
      breadcrumbs: [
        { name: "Início", path: "/" },
        { name: "Contato", path: "/contato" },
      ],
    }),
  component: ContatoPage,
  pendingComponent: ContatoSkeleton,
});

const SUPPORT_EMAIL = "suporte@seubolsotranquilo.com";
const RATE_LIMIT_MS = 3 * 60 * 1000; // 3 minutos
const RATE_LIMIT_KEY = "contato:lastSentAt";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Informe seu nome").max(100),
  email: z.string().trim().email("Email inválido").max(255),
  subject: z.string().trim().min(3, "Informe um assunto").max(150),
  message: z.string().trim().min(10, "Mensagem muito curta").max(2000),
});

function ContatoPage() {
  const { user, displayName } = useAuthCtx();
  const [name, setName] = useState(displayName ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cooldownLeft, setCooldownLeft] = useState(0);

  // Inicializa e mantém o cooldown a partir do localStorage
  useEffect(() => {
    const tick = () => {
      try {
        const last = Number(localStorage.getItem(RATE_LIMIT_KEY) ?? 0);
        if (!last) {
          setCooldownLeft(0);
          return;
        }
        const remaining = Math.max(0, RATE_LIMIT_MS - (Date.now() - last));
        setCooldownLeft(remaining);
      } catch {
        setCooldownLeft(0);
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (cooldownLeft > 0) {
      const seconds = Math.ceil(cooldownLeft / 1000);
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      const label = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
      toast.error(`Aguarde ${label} antes de enviar outra mensagem.`);
      return;
    }
    const parsed = contactSchema.safeParse({ name, email, subject, message });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Verifique os campos.");
      return;
    }
    setSubmitting(true);
    try {
      const body = encodeURIComponent(
        `Nome: ${parsed.data.name}\nEmail: ${parsed.data.email}\n\n${parsed.data.message}`,
      );
      const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
        `[Suporte] ${parsed.data.subject}`,
      )}&body=${body}`;
      window.location.href = mailto;
      toast.success("Abrindo seu cliente de email para enviar a mensagem.");
      try {
        localStorage.setItem(RATE_LIMIT_KEY, String(Date.now()));
      } catch {
        // ignora se localStorage não estiver disponível
      }
      setCooldownLeft(RATE_LIMIT_MS);
      setSubject("");
      setMessage("");
    } finally {
      setSubmitting(false);
    }
  };

  const cooldownLabel = (() => {
    if (cooldownLeft <= 0) return null;
    const seconds = Math.ceil(cooldownLeft / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs.toString().padStart(2, "0")}s` : `${secs}s`;
  })();

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-6 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="size-4" /> Voltar
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <BrandLogo className="size-10" />
          <div>
            <h1 className="font-display text-3xl font-bold leading-tight">Fale com a gente</h1>
            <p className="text-sm text-muted-foreground">
              O formulário abre seu app de email com tudo preenchido. Respondemos em até 1 dia útil.
            </p>
          </div>
        </div>

        <div className="rounded-3xl border border-border/40 bg-gradient-card p-6 lg:p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor="contato-name" className="text-xs font-medium text-muted-foreground">Nome</label>
                <input
                  id="contato-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={100}
                  className="w-full px-4 py-3 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="contato-email" className="text-xs font-medium text-muted-foreground">Email</label>
                <input
                  id="contato-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  maxLength={255}
                  className="w-full px-4 py-3 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="contato-subject" className="text-xs font-medium text-muted-foreground">Assunto</label>
              <input
                id="contato-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Como podemos ajudar?"
                required
                maxLength={150}
                className="w-full px-4 py-3 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="contato-message" className="text-xs font-medium text-muted-foreground">Mensagem</label>
              <textarea
                id="contato-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Conte detalhes do que você precisa..."
                required
                minLength={10}
                maxLength={2000}
                rows={6}
                className="w-full px-4 py-3 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all resize-none"
              />
              <p className="text-[11px] text-muted-foreground">{message.length}/2000</p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <Mail className="size-4" /> {SUPPORT_EMAIL}
              </a>
              <button
                type="submit"
                disabled={submitting || cooldownLeft > 0}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {submitting && <Loader2 className="size-4 animate-spin" />}
                {cooldownLabel ? `Aguarde ${cooldownLabel}` : "Abrir meu email para enviar"}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground sm:text-right">
              Ao clicar, abrimos seu aplicativo de email com a mensagem já preenchida para {SUPPORT_EMAIL}. O envio só é
              concluído quando você confirmar no seu email — respondemos em até 1 dia útil a partir daí.
            </p>
            {cooldownLabel && (
              <p className="text-[11px] text-muted-foreground text-right">
                Para evitar envios duplicados, espere {cooldownLabel} antes de enviar outra mensagem.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}